<template>
  <div class="loan-emi-schedule">
    <!-- No EMIs message -->
    <!-- Accept Installment Header -->
    <div class="flex justify-between items-center mb-6">
      <h3 class="text-xl font-bold text-gray-800">EMI Schedule</h3>
      <div class="flex justify-end gap-2">
        <v-btn
          color="success"
          depressed
          small
          class="font-weight-black rounded-lg px-6 mr-2"
          @click="exportToExcel"
          :disabled="!emiSchedule.length"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Download Emi Schedule
        </v-btn>
        <v-btn
          color="primary"
          depressed
          small
          class="font-weight-black rounded-lg px-6"
          @click="showAcceptInstallment = true"
          :disabled="isLoanSettled"
          v-iam-disabled="
            `${getDynamicPermissions().useCase}.${
              getDynamicPermissions().subCase
            }.emi_schedule.write`
          "
        >
          <v-icon left>{{ isLoanSettled ? 'mdi-lock' : 'mdi-plus' }}</v-icon>
          {{ isLoanSettled ? 'Loan Settled' : 'Accept Installment' }}
        </v-btn>
      </div>
    </div>

    <!-- Accept Installment Dialog -->
    <v-dialog v-model="showAcceptInstallment" max-width="850px" persistent overlay-opacity="0.8">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="d-flex justify-space-between align-center px-6 py-4 border-b">
          <div class="d-flex align-center">
            <div class="bg-blue-50 p-2 rounded-lg mr-3">
              <v-icon color="primary">mdi-cash-register</v-icon>
            </div>
            <span class="text-lg font-bold text-gray-900 uppercase tracking-widest"
              >EMI Collection</span
            >
          </div>
          <v-btn icon @click="showAcceptInstallment = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <div class="p-6">
            <!-- Summary Grid (Compact) -->
            <div
              class="grid grid-cols-2 md:grid-cols-5 gap-4 bg-slate-50 p-4 rounded-lg border border-gray-100 mb-6"
            >
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >Due</span
                >
                <span class="text-sm font-black text-gray-900"
                  >₹{{ formatCurrency(paymentSummary.installmentDue) }}</span
                >
              </div>
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >Est. LPC</span
                >
                <span class="text-sm font-black text-gray-900"
                  >₹{{ formatCurrency(paymentSummary.estLpc) }}</span
                >
              </div>
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >VAS</span
                >
                <span class="text-sm font-black text-gray-900"
                  >₹{{ formatCurrency(paymentSummary.estVas) }}</span
                >
              </div>
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >Charges</span
                >
                <span class="text-sm font-black text-gray-900"
                  >₹{{ formatCurrency(paymentSummary.dueCharges) }}</span
                >
              </div>
              <div class="flex flex-col border-l pl-4">
                <span class="text-[10px] font-bold text-primary uppercase tracking-wider mb-1"
                  >Total</span
                >
                <span class="text-sm font-black text-primary"
                  >₹{{ formatCurrency(paymentSummary.total) }}</span
                >
              </div>
            </div>

            <v-form ref="paymentForm" class="payment-fields-compact">
              <v-row dense>
                <!-- Mode of Payment -->
                <v-col cols="12" md="6" class="pr-2">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Mode of Payment *
                  </div>
                  <v-select
                    v-model="form.mode"
                    :items="['Cash', 'Bank', 'Transfer']"
                    outlined
                    dense
                    hide-details
                    placeholder="Select Mode"
                    class="rounded-lg text-sm"
                  ></v-select>
                </v-col>

                <!-- Ledger / From Account -->
                <v-col cols="12" md="6" class="pl-2">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    {{ form.mode === 'Transfer' ? 'From Account' : 'Ledger' }} *
                  </div>
                  <v-autocomplete
                    v-model="form.ledgerId"
                    :items="accounts"
                    item-text="name"
                    item-value="_id"
                    :search-input.sync="form.search"
                    outlined
                    dense
                    hide-details
                    placeholder="Search Account"
                    class="rounded-lg text-sm font-weight-bold"
                  ></v-autocomplete>
                </v-col>

                <!-- Amount -->
                <v-col cols="12" md="6" class="pr-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Amount *
                  </div>
                  <v-text-field
                    v-model.number="form.amount"
                    type="number"
                    outlined
                    dense
                    hide-details
                    placeholder="0.00"
                    class="rounded-lg text-sm font-weight-black"
                  ></v-text-field>
                </v-col>

                <!-- Collection Charges -->
                <v-col cols="12" md="6" class="pl-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Collection Charges
                  </div>
                  <v-text-field
                    v-model.number="form.collectionCharges"
                    type="number"
                    outlined
                    dense
                    hide-details
                    placeholder="0.00"
                    class="rounded-lg text-sm"
                  ></v-text-field>
                </v-col>

                <!-- Date -->
                <v-col cols="12" md="6" class="pr-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Date *
                  </div>
                  <v-text-field
                    v-model="form.date"
                    type="date"
                    outlined
                    dense
                    hide-details
                    class="rounded-lg text-sm"
                  ></v-text-field>
                </v-col>

                <!-- Channel (Bank only) -->
                <v-col v-if="form.mode === 'Bank'" cols="12" md="6" class="pr-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Channel *
                  </div>
                  <v-select
                    v-model="form.channel"
                    :items="['Cheque', 'NEFT', 'RTGS', 'IMPS', 'UPI']"
                    outlined
                    dense
                    hide-details
                    placeholder="Select Channel"
                    class="rounded-lg text-sm"
                  ></v-select>
                </v-col>

                <!-- Voucher No -->
                <v-col cols="12" md="6" class="pl-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Voucher No
                  </div>
                  <v-text-field
                    v-model="form.voucherNo"
                    outlined
                    dense
                    hide-details
                    readonly
                    background-color="grey lighten-4"
                    class="rounded-lg text-sm"
                  ></v-text-field>
                </v-col>

                <!-- Comments -->
                <v-col cols="12" class="mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Comments
                  </div>
                  <v-text-field
                    v-model="form.comments"
                    outlined
                    dense
                    hide-details
                    placeholder="Add collection notes..."
                    class="rounded-lg text-sm"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="px-6 py-4 bg-gray-50 d-flex gap-3">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="normal-case font-weight-bold px-6 border"
            @click="showAcceptInstallment = false"
          >
            Cancel
          </v-btn>
          <!-- <v-btn
            color="primary"
            outlined
            class="rounded-lg font-weight-black px-6"
            @click="handleAccept(true)"
          >
            ACCEPT & PRINT
          </v-btn> -->
          <v-btn
            color="primary"
            depressed
            class="rounded-lg font-weight-black px-10"
            @click="handleAccept(false)"
          >
            ACCEPT
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payment Transactions / Receipts History -->
    <v-card
      v-if="
        loanDetails &&
        loanDetails.paymentSummaries &&
        loanDetails.paymentSummaries.filter((s) => s.paymentType === 'Installment').length > 0
      "
      class="mb-6 rounded-xl"
      outlined
    >
      <v-card-title
        class="pb-2 text-base font-bold text-gray-800 d-flex justify-space-between align-center"
      >
        <div class="d-flex align-center">
          <v-icon left color="primary">mdi-history</v-icon>
          Payment Transactions / Receipts History
        </div>
      </v-card-title>
      <v-card-text class="pt-2">
        <div class="overflow-x-auto overflow-y-auto" style="max-height: 300px">
          <table class="w-full text-sm text-left border rounded-lg">
            <thead class="bg-gray-50 border-b">
              <tr>
                <th class="p-3 font-semibold text-gray-600">Date</th>
                <th class="p-3 font-semibold text-gray-600">Voucher No</th>
                <th class="p-3 font-semibold text-gray-600">Amount Paid</th>
                <th class="p-3 font-semibold text-gray-600">Payment Method</th>
                <th class="p-3 font-semibold text-gray-600">Status</th>
                <th class="p-3 font-semibold text-gray-600 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(summary, i) in loanDetails.paymentSummaries
                  .filter((s) => s.paymentType === 'Installment')
                  .slice()
                  .reverse()"
                :key="summary.voucherNo"
                class="border-b transition-all"
                :class="summary.isDeleted ? 'bg-red-50/50 opacity-60' : 'hover:bg-gray-50'"
              >
                <td class="px-3 whitespace-nowrap">
                  <span :class="{ 'line-through text-red-600': summary.isDeleted }">
                    {{ formatDate(summary.paymentDate) }}
                  </span>
                  <v-chip
                    v-if="summary.isDeleted"
                    x-small
                    color="red"
                    dark
                    label
                    class="ml-1 font-bold"
                    >Reverted</v-chip
                  >
                </td>
                <td class="px-3" :class="{ 'line-through opacity-50': summary.isDeleted }">
                  {{ summary.voucherNo }}
                </td>
                <td
                  class="px-3 font-medium"
                  :class="{ 'line-through opacity-50': summary.isDeleted }"
                >
                  ₹{{ formatCurrency(summary.amountPaid) }}
                </td>
                <td class="px-3" :class="{ 'line-through opacity-50': summary.isDeleted }">
                  {{ summary.paymentMethod }}
                </td>
                <td class="px-3">
                  <v-chip
                    x-small
                    :color="summary.isDeleted ? 'red' : 'green'"
                    text-color="white"
                    class="font-bold"
                  >
                    {{ summary.isDeleted ? 'Reverted' : 'Active' }}
                  </v-chip>
                </td>
                <td class="px-3 py-1 text-center">
                  <v-btn
                    v-if="!summary.isDeleted"
                    :disabled="i !== 0"
                    x-small
                    color="red"
                    outlined
                    class="text-none font-bold rounded-lg"
                    @click="confirmRevert(null, summary)"
                    v-iam-disabled="
                      `${getDynamicPermissions().useCase}.${
                        getDynamicPermissions().subCase
                      }.emi_schedule.delete`
                    "
                  >
                    <v-icon left x-small>mdi-undo</v-icon> Revert
                  </v-btn>
                  <span v-else class="text-xs text-gray-500">Reverted</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>
    </v-card>

    <div v-if="!emiSchedule.length" class="text-center py-8">
      <v-icon size="64" color="grey lighten-1">mdi-calendar-remove</v-icon>
      <div class="mt-4 text-gray-600">
        No EMI schedule available for {{ borrowerName || 'this borrower' }}
      </div>
    </div>

    <v-expansion-panels v-else v-model="expandedPanel">
      <v-expansion-panel
        v-for="(emi, i) in emiSchedule"
        :key="emi._id"
        class="mb-3"
        :class="{ 'bg-red-50/30': emi.isDeleted }"
      >
        <v-expansion-panel-header :class="{ 'opacity-70': emi.isDeleted }">
          <div class="w-full grid grid-cols-10 gap-2 text-center items-center">
            <div>
              <p class="text-sm font-medium text-gray-600">EMI No.</p>
              <div class="flex items-center justify-center">
                <p class="font-semibold" :class="{ 'line-through text-red-600': emi.isDeleted }">
                  {{ i + 1 }}
                </p>
                <v-chip v-if="emi.isDeleted" x-small color="red" dark class="ml-1 px-1 font-bold"
                  >DELETED</v-chip
                >
              </div>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">Due Date</p>
              <p class="font-semibold">{{ formatDate(emi.dueDate) }}</p>
              <div
                v-if="getOverdueInfo(emi).isOverdue || getOverdueInfo(emi).isLatePaid"
                class="mt-1"
              >
                <v-chip
                  small
                  outlined
                  label
                  class="font-bold"
                  :color="
                    getOverdueInfo(emi).isLatePaid
                      ? getStatusColor('late_paid').textColor
                      : getStatusColor('overdue').textColor
                  "
                >
                  <v-icon left x-small>
                    {{ getOverdueInfo(emi).isLatePaid ? 'mdi-clock-alert' : 'mdi-alert-circle' }}
                  </v-icon>
                  {{
                    getOverdueInfo(emi).isLatePaid
                      ? getOverdueInfo(emi).latePaidText
                      : getOverdueInfo(emi).overdueText
                  }}
                </v-chip>
              </div>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">@Interest</p>
              <p class="font-semibold">{{ emi?.atRateOfInterest }}%</p>
            </div>
            <div :class="{ 'opacity-60': emi.isDeleted }">
              <p class="text-sm font-medium text-gray-600">EMI Amount</p>
              <p class="font-semibold" :class="{ 'line-through': emi.isDeleted }">
                ₹{{ formatCurrency(emi.emiAmount) }}
              </p>
              <div v-if="getOverdueInfo(emi).lateFeeAmount > 0" class="mt-1">
                <v-chip
                  outlined
                  label
                  small
                  class="text-xs font-bold"
                  :color="getStatusColor('late_fee').textColor"
                >
                  Late Fee: ₹{{ formatCurrency(getOverdueInfo(emi).lateFeeAmount) }}
                </v-chip>
              </div>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">Principal</p>
              <p class="font-semibold">₹{{ formatCurrency(emi.principalAmount) }}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">Interest</p>
              <p class="font-semibold">₹{{ formatCurrency(emi.interestAmount) }}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">Principal Outstanding</p>
              <p class="font-semibold">₹{{ formatCurrency(emi.outstandingAmount) }}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">PDD Status</p>
              <v-chip small outlined label :color="getPDDStatus(emi.activities)?.color">
                {{ getPDDStatus(emi.activities).status }}
              </v-chip>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">EMI Status</p>
              <v-chip
                small
                label
                outlined
                class="font-bold"
                v-if="emi.emiStatus !== 'Minimum Amount Paid'"
                :color="getStatusColor(emi?.emiStatus)?.textColor"
              >
                {{ emi.emiStatus }}
              </v-chip>
              <div v-else class="mt-1">
                <v-chip
                  small
                  :color="getStatusColor('minimum_amount_paid')?.textColor"
                  class="font-bold"
                  outlined
                  label
                >
                  {{ emi?.emiStatus }}
                </v-chip>
                <div class="mt-1">
                  <v-chip
                    small
                    :color="getStatusColor('minimum_amount_paid')?.textColor"
                    class="font-bold"
                    outlined
                    label
                  >
                    ₹{{ formatCurrency(calculateMinimumAmountForEmi(emi)) }}
                  </v-chip>
                </div>
              </div>
            </div>
            <!-- Direct Receipt Download Column -->
            <div @click.stop class="flex flex-col items-center justify-center">
              <p class="text-sm font-medium text-gray-600 mb-1">Receipt</p>
              <div
                v-if="getActivePayments(emi).length > 0"
                class="flex items-center justify-center"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      color="primary"
                      outlined
                      class="text-none font-bold rounded-lg px-2 h-7"
                      v-bind="attrs"
                      v-on="on"
                      @click.stop="generateReceipt(emi, getActivePayments(emi), i, 'download')"
                      :loading="isGeneratingPDF === `emi-${i}`"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      {{ `Download (${getActivePayments(emi).length})` }}
                    </v-btn>
                  </template>
                  <span>
                    Download
                    {{
                      getActivePayments(emi).length > 1
                        ? `all ${getActivePayments(emi).length} receipts in 1 PDF`
                        : 'receipt PDF'
                    }}
                    (₹{{ formatCurrency(getEmiTotalReceiptsAmount(emi)) }})
                  </span>
                </v-tooltip>
              </div>
              <span v-else class="text-xs text-gray-400 font-medium">—</span>
            </div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="activities-container mt-4">
            <!-- Revenue Share Information -->
            <div v-if="revenueShare && Object.keys(revenueShare).length" class="mb-6">
              <h4 class="text-md font-semibold mb-3">Revenue Breakdown</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Left Card: Partner & Broker Revenue Share -->
                <div class="p-4 bg-blue-50 rounded-lg border">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">Profit Details</h5>
                  <div class="flex justify-between space-x-3">
                    <div class="text-center w-1/3">
                      <p class="text-sm font-medium text-gray-600">
                        {{ partner?.partnerName }} (Partner)
                      </p>
                      <p class="text-sm font-bold text-blue-600">
                        ₹{{ formatCurrency(calculatePartnerShare(emi)) }}
                      </p>
                      <p class="text-xs text-gray-500">
                        ({{ revenueShare.hurdleRate }}/{{ emiSchedule.length }} % Hurdle Rate +
                        {{ revenueShare.partnerShare }}% Bonus Profit)
                      </p>
                    </div>
                    <v-divider vertical></v-divider>
                    <div class="text-center w-1/3">
                      <p class="text-sm font-medium text-gray-600">{{ orgName }} (Broker)</p>
                      <p class="text-sm font-bold text-green-600">
                        ₹{{
                          formatCurrency(
                            calculateBrokerShare(
                              emi,
                              revenueShare,
                              getTotalPrincipal(),
                              emiSchedule?.length
                            )
                          )
                        }}
                      </p>
                      <p class="text-xs text-gray-500">
                        ({{ revenueShare.brokerShare }}% Bonus Profit)
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Right Card: Actual Amount Calculation -->
                <div class="p-4 bg-green-50 rounded-lg border">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">Amount Distribution</h5>
                  <div class="space-y-3">
                    <div class="text-center pt-2">
                      <p class="text-sm text-gray-600">
                        Total EMI Amount - Broker Profit = Partner EMI
                      </p>
                      <p class="text-sm font-medium text-gray-700">
                        ₹{{ formatCurrency(emi.emiAmount) }} - ₹{{
                          formatCurrency(calculateBrokerShare(emi))
                        }}
                        =
                        <span class="text-orange-600">
                          ₹{{ formatCurrency(calculateActualPartnerAmount(emi)) }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Payment details section -->
            <div v-if="emi.emiPayments && emi.emiPayments.length" class="mb-6">
              <h4 class="text-md font-semibold mb-3">Payment History</h4>
              <div class="border rounded-lg overflow-hidden shadow-sm">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr class="bg-gray-100">
                        <th class="text-left px-4 py-3 font-bold text-gray-700">Date</th>
                        <th class="text-left px-4 py-3 font-bold text-gray-700">Receipt No</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Amount</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Principal</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Late Fee</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Coll. Charges</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">LPC Due</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Int. O/S</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Advance</th>
                        <th class="text-center px-4 py-3 font-bold text-gray-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(payment, pIdx) in emi.emiPayments"
                        :key="pIdx"
                        :class="[
                          'hover:bg-gray-50 transition-colors',
                          payment.isDeleted ? 'bg-red-50 text-gray-500 opacity-70' : ''
                        ]"
                      >
                        <td class="px-4 py-3">
                          <div class="flex items-center space-x-2">
                            <span :class="{ 'strike-through-text': payment.isDeleted }">
                              {{ formatDate(payment.date) }}
                            </span>
                            <v-chip
                              v-if="payment.isDeleted"
                              x-small
                              color="red lighten-4"
                              text-color="red darken-4"
                              class="font-bold"
                            >
                              DELETED
                            </v-chip>
                          </div>
                        </td>
                        <td class="px-4 py-3 font-medium text-gray-700">
                          <span :class="{ 'strike-through-text': payment.isDeleted }">
                            {{ payment.voucherNo || '-' }}
                          </span>
                        </td>
                        <td
                          class="text-right px-4 py-3 font-bold"
                          :class="payment.isDeleted ? 'text-gray-500' : 'text-blue-700'"
                        >
                          <span :class="{ 'strike-through-text': payment.isDeleted }">
                            ₹{{ formatCurrency(payment.amount) }}
                          </span>
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.principalAmount) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.latePaymentAmount) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.collectionCharges) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.lpcDueAmount) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.interestOutStandingAmount) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.receiveAdvanceAmount) }}
                        </td>
                        <td class="text-center px-4 py-3">
                          <div
                            class="d-flex align-center justify-center gap-1"
                            v-if="!payment.isDeleted"
                          >
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  small
                                  color="primary"
                                  v-bind="attrs"
                                  v-on="on"
                                  @click.stop="generateReceipt(emi, payment, i, 'download')"
                                  :loading="isGeneratingPDF === payment._id"
                                >
                                  <v-icon small>mdi-printer</v-icon>
                                </v-btn>
                              </template>
                              <span>
                                Download {{ payment.voucherNo || 'Receipt' }} (₹{{
                                  formatCurrency(getReceiptTotalAmount(payment))
                                }})
                              </span>
                            </v-tooltip>

                            <!-- <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  small
                                  color="error"
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="confirmRevert(emi, payment)"
                                  v-iam-disabled="
                                    `${getDynamicPermissions().useCase}.${
                                      getDynamicPermissions().subCase
                                    }.emi_schedule.delete`
                                  "
                                >
                                  <v-icon small>mdi-delete-outline</v-icon>
                                </v-btn>
                              </template>
                              <span>Revert Payment</span>
                            </v-tooltip> -->
                          </div>
                          <v-icon v-else color="grey lighten-1">mdi-cancel</v-icon>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </div>

            <div class="flex justify-between items-center mb-3">
              <h4 class="text-md font-semibold">
                {{ emi.activities && emi.activities.length ? 'Requirements' : '' }}
              </h4>
              <!-- <v-btn
                small
                color="primary"
                outlined
                @click="openEditEmiDialog(emi._id)"
                :disabled="isLoanSettled"
              >
                <v-icon left small>mdi-pencil</v-icon>
                Edit EMI Details
              </v-btn> -->
            </div>

            <template v-if="emi.activities && emi.activities.length">
              <div
                v-for="(group, stepName) in groupActivitiesByStepName(emi.activities)"
                :key="stepName"
              >
                <h5 class="text-sm font-bold text-gray-500 mb-2 mt-4">{{ stepName }}</h5>
                <div
                  v-for="activity in group"
                  :key="activity._id"
                  class="activity-item mb-3 border rounded-md bg-gray-50 dark-bg-custom"
                >
                  <div class="flex justify-between items-center pl-2 pr-2">
                    <div class="flex items-center justify-between w-full">
                      <p class="font-medium text-sm pt-3">{{ activity.activityType }}</p>
                      <div class="flex items-center space-x-2 justify-end">
                        <p class="mb-0 text-xs text-gray-600">
                          Last Updated: {{ formatDateTime(activity.updatedAt) }}
                        </p>
                        <v-chip
                          small
                          label
                          outlined
                          :color="getActivityStatusColor(activity.status)"
                        >
                          {{ activity.status }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                  <div v-if="activity.activityDoc" class="mt-3">
                    <div class="flex items-center">
                      <v-icon size="20" color="primary" class="mr-2"
                        >mdi-file-document-outline</v-icon
                      >
                      <a
                        :href="activity.activityDoc"
                        target="_blank"
                        class="text-blue-600 hover:underline"
                      >
                        View Document
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Edit EMI Dialog -->
    <AddEditEmi
      v-model="editEmiDialog"
      :emi-data="selectedEmi"
      :is-loan-settled="isLoanSettled"
      @save-emi="handleEmiUpdate"
      @show-notification="$emit('show-notification', $event)"
    />

    <!-- Hidden Receipt for Printing -->
    <EmiPaymentReceipt
      ref="receiptRef"
      v-if="receiptData.payment"
      :payment="receiptData.payment"
      :customer-name="receiptData.customerName"
      :agreement-date="receiptData.agreementDate"
      :agreement-no="receiptData.agreementNo"
      :emi-no="receiptData.emiNo"
      :total-emis="emiSchedule.length"
      :emi-due-date="receiptData.emiDueDate"
      :emi-amount="receiptData.emiAmount"
      :cashier-name="receiptData.cashierName"
    />
    <!-- Revert Confirmation Dialog -->
    <v-dialog v-model="revertDialog" max-width="450px">
      <v-card class="overflow-hidden">
        <p class="bg-red-50 text-red-800 py-2 px-4 font-bold">Revert Installment Payment?</p>
        <p class="pa-2 text-gray-600">
          Are you sure you want to revert the payment amount of
          <span class="font-bold text-gray-800"
            >₹{{ formatCurrency(selectedPaymentToRevert?.amount || 0) }}</span
          >
          with Voucher No
          <span class="font-bold text-gray-800"
            >"{{ selectedPaymentToRevert?.voucherNo || '-' }}"</span
          >? This will revert the status and balances of all adjusted EMIs and soft-delete the
          accounting entries.
        </p>
        <v-card-actions class="pa-4 bg-gray-50">
          <v-spacer></v-spacer>
          <v-btn text @click="revertDialog = false" class="normal-case">Cancel</v-btn>
          <v-btn
            color="error"
            depressed
            @click="submitRevert"
            :loading="reverting"
            class="normal-case rounded-lg px-6 font-bold shadow-sm"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { calculateOverdueDays, calculateBrokerShareAmount } from '@/utils/common'
import { chipStatusColors } from '@/utils/workforce/statusColors'
import AddEditEmi from './VisitModules/AddEditEmi.vue'
import EmiPaymentReceipt from './EmiPaymentReceipt.vue'
import html2pdf from 'html2pdf.js'
import { debounce } from 'lodash'
import * as XLSX from 'xlsx'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'

// Initialize pdfmake fonts safely
pdfMake.vfs = pdfFonts && pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts

export default {
  name: 'LoanEmiScheduleView',
  components: {
    AddEditEmi,
    EmiPaymentReceipt
  },
  props: {
    emiSchedule: {
      type: Array,
      required: true
    },
    borrowerId: {
      type: String,
      default: null
    },
    revenueShare: {
      type: Object,
      default: () => {}
    },
    partner: {
      type: Object,
      default: () => {}
    },
    selectedCase: {
      type: Object,
      default: () => null
    }
  },
  inject: {
    getDynamicPermissions: {
      default: () => ({ useCase: '', subCase: '' })
    }
  },
  data() {
    return {
      orgName: this.$storage.getUniversal('organization_code'),
      expandedPanel: null,
      editEmiDialog: false,
      selectedEmi: {},
      isGeneratingPDF: null,
      receiptData: {
        payment: null,
        customerName: '',
        agreementDate: '',
        agreementNo: '',
        emiNo: 1,
        emiDueDate: '',
        emiAmount: 0,
        cashierName: ''
      },
      showAcceptInstallment: false,
      accounts: [],
      revertDialog: false,
      selectedPaymentToRevert: null,
      reverting: false,
      form: {
        mode: 'Cash',
        ledgerId: null,
        amount: 0,
        collectionCharges: 0,
        date: new Date().toISOString().substr(0, 10),
        instrumentNo: '',
        voucherNo: '',
        comments: '',
        sendSms: true,
        channel: null,
        search: ''
      }
    }
  },
  computed: {
    loanDetails() {
      return this.selectedCase?.loanDetails || null
    },
    borrowerName() {
      return this.borrowerId || 'Unknown Borrower'
    },
    paymentSummary() {
      const unpaidEmis = this.emiSchedule.filter(
        (e) => !['Paid', 'Late', 'Late Paid', 'Fully Paid', 'Waived Off', 'Cancelled'].includes(e.emiStatus) && !e.isPaid && !e.isDeleted
      )

      let installmentDue = 0
      unpaidEmis.forEach((emi) => {
        const emiPaid = (emi.emiPayments || [])
          .filter((p) => !p.isDeleted)
          .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
        installmentDue += Math.max(0, (parseFloat(emi.emiAmount) || 0) - emiPaid)
      })

      let estLpc = 0
      const today = this.$dayjs().startOf('day')
      const loanDetails = this.selectedCase?.loanDetails
      const feeType = loanDetails?.lateFeeType || 'Fixed'
      const feeVal =
        loanDetails?.latePaymentFee !== undefined && loanDetails?.latePaymentFee !== null
          ? parseFloat(loanDetails.latePaymentFee)
          : 0
      const paidStatuses = ['Paid', 'Late', 'Late Paid', 'Minimum Amount Paid']

      this.emiSchedule
        .filter((e) => !e.isDeleted && !e.isPaid && !['Waived Off', 'Cancelled'].includes(e.emiStatus))
        .forEach((emi) => {
          const activePayments = (emi.emiPayments || []).filter((p) => !p.isDeleted)
          const receipt = activePayments.reduce(
            (sum, p) => sum + (parseFloat(p.latePaymentAmount) || 0),
            0
          )

          let totalLpi = 0
          const dueDate = this.$dayjs(emi.dueDate).startOf('day')

          const isMonthlyLogic = feeVal === 600

          const calcLpcFee = (days, refDate) => {
            if (!feeVal || days <= 0) return 0
            if (feeType === 'Fixed' || feeType === 'Flat') {
              if (isMonthlyLogic) {
                const diffMonths = this.$dayjs(refDate).diff(dueDate, 'month')
                const targetDatePlusMonths = this.$dayjs(dueDate).add(diffMonths, 'month')
                const extraDays = this.$dayjs(refDate).diff(targetDatePlusMonths, 'day')
                const chargeableMonths = Math.max(1, diffMonths + (extraDays >= 15 ? 1 : 0))
                return feeVal * chargeableMonths
              }
              return feeVal * days
            }
            if (feeType === 'Percentage') {
              const year = this.$dayjs(refDate).year()
              const yearDays = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 366 : 365
              return (parseFloat(emi.emiAmount || 0) * feeVal * (days / yearDays)) / 100
            }
            return 0
          }

          if (emi.emiStatus === 'Paid' || emi.emiStatus === 'Waived Off' || emi.emiStatus === 'Cancelled') {
            // Paid on time — no LPC
            totalLpi = 0
          } else if (
            emi.emiStatus === 'Late' ||
            emi.emiStatus === 'Late Paid' ||
            emi.emiStatus === 'Minimum Amount Paid'
          ) {
            // Paid but after due date — calculate from actual payment date
            const payDate = emi.paymentDate
              ? this.$dayjs(emi.paymentDate).startOf('day')
              : activePayments[0]?.date
              ? this.$dayjs(activePayments[0].date).startOf('day')
              : dueDate
            const lateDays = Math.max(0, payDate.diff(dueDate, 'day'))
            totalLpi = calcLpcFee(lateDays, payDate.toDate())
          } else {
            // Pending / Overdue: calculate dynamically from today
            if (today.isAfter(dueDate)) {
              const lateDays = today.diff(dueDate, 'day')
              totalLpi = calcLpcFee(lateDays, today.toDate())
            }
          }

          totalLpi = Math.max(totalLpi, receipt)

          estLpc += Math.max(0, totalLpi - receipt)
        })

      const estVas = (this.selectedCase?.loanDetails?.additionalCharges || [])
        .filter(
          (c) =>
            !c.isDeleted &&
            !c.isDeduction &&
            c.chargeType === 'CHARGE' &&
            !['Paid', 'Fully Paid'].includes(c.status)
        )
        .reduce((sum, charge) => {
          const net =
            (parseFloat(charge.amount) || 0) -
            (parseFloat(charge.metaData?.discountAmount) || 0) -
            (parseFloat(charge.metaData?.waiverAmount) || 0)
          const unpaid = Math.max(0, net - (parseFloat(charge.paidAmount) || 0))
          return sum + unpaid
        }, 0)
      const dueCharges = 0.0

      const total = installmentDue + estLpc + estVas + dueCharges

      return {
        installmentDue,
        estLpc,
        estVas,
        dueCharges,
        total
      }
    },
    isLoanSettled() {
      const status =
        this.selectedCase?.loanDetails?.currentStatus || this.selectedCase?.status || ''
      return ['Settled', 'Completed', 'Closed', 'Foreclosed'].includes(status)
    }
  },
  watch: {
    'paymentSummary.total': {
      handler(newVal) {
        if (this.form.amount === 0) {
          this.form.amount = newVal
        }
      },
      immediate: true
    },
    'form.mode': {
      handler() {
        this.fetchAccounts()
      },
      immediate: true
    },
    'form.search': {
      handler(val) {
        if (val) {
          this.debouncedFetchAccounts()
        }
      }
    }
  },
  created() {
    this.debouncedFetchAccounts = debounce(this.fetchAccounts, 500)
  },
  async mounted() {
    // Initial fetch handled by immediate watcher on form.mode
  },
  methods: {
    async fetchAccounts() {
      const mode = this.form.mode
      const modeMapping = {
        Cash: 2194,
        Bank: 2195,
        Transfer: 2190
      }
      const ledgerGroupClientId = modeMapping[mode]

      if (!ledgerGroupClientId) {
        this.accounts = []
        return
      }

      try {
        const params = {
          LedgerGroupClientId: ledgerGroupClientId,
          restrictionTypeId: 1
        }
        if (this.form.search) {
          params.name = this.form.search
        }
        const response = await this.$axios.get('/orgSetup/accounts-management', { params })
        this.accounts = response.data.accountsManagements || []
      } catch (error) {
        console.error('Error fetching accounts:', error)
      }
    },
    async handleAccept(print = false) {
      // Basic validation
      if (!this.form.ledgerId || !this.form.amount) {
        this.$emit('show-notification', { message: 'Please fill required fields', color: 'error' })
        return
      }

      const payload = {
        ...this.form,
        borrowerId: this.borrowerId,
        caseId: this.selectedCase?._id
      }

      console.log('Accepting payment:', payload)

      // Emit event to parent to handle the actual API call
      this.$emit('accept-installment', { payload, print })

      // Reset form if success
      this.showAcceptInstallment = false
      this.$emit('show-notification', {
        message: 'Installment accepted successfully',
        color: 'success'
      })
    },

    groupActivitiesByStepName(activities) {
      if (!activities || !activities.length) return {}
      return activities.reduce((acc, activity) => {
        const step = activity.activityStepName || 'Other'
        if (!acc[step]) acc[step] = []
        acc[step].push(activity)
        return acc
      }, {})
    },
    formatDate(dateString) {
      return this.$dayjs(dateString).format('DD MMM YYYY')
    },

    formatDateTime(dateString) {
      return this.$dayjs(dateString).format('DD MMM YYYY, hh:mm A')
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-IN').format(value)
    },
    getPDDStatus(activities) {
      if (activities?.length) {
        const isAllStepsCompleted = activities.every((activity) => activity.status === 'Completed')
        return isAllStepsCompleted
          ? { status: 'Completed', color: this.getStatusColor('completed')?.textColor }
          : { status: 'Not Completed', color: this.getStatusColor('not_completed')?.textColor }
      }
      return { status: 'No PDD Steps', color: 'grey' }
    },

    getStatusColor(status) {
      if (!status) return { textColor: '#999999' }
      const normalized = String(status).toLowerCase()
      const underscoreKey = normalized.replace(/\s+/g, '_')
      return (
        chipStatusColors[normalized] ||
        chipStatusColors[underscoreKey] || { textColor: '#8B5CF6' }
      )
    },

    getOverdueInfo(emi) {
      return calculateOverdueDays(
        emi.dueDate,
        emi.emiStatus,
        emi.paymentDate,
        emi.emiAmount,
        this.selectedCase?.loanDetails?.lateFeeType || emi.lateFeeType || 'Fixed',
        this.selectedCase?.loanDetails?.latePaymentFee !== undefined
          ? parseFloat(this.selectedCase.loanDetails.latePaymentFee)
          : emi.lateFeeValue !== undefined
          ? parseFloat(emi.lateFeeValue)
          : 0,
        this.selectedCase?.loanDetails?.workflows?.find((w) => w.workflowType === 'loan')
          ?.template || null
      )
    },

    getActivityStatusColor(status) {
      switch (status) {
        case 'Completed':
          return this.getStatusColor('completed')?.textColor
        case 'Not Completed':
          return this.getStatusColor('not_completed')?.textColor
        case 'In Progress':
          return this.getStatusColor('inProgress')?.textColor
        default:
          return 'grey darken-2'
      }
    },

    openEditEmiDialog(emiId) {
      const emi = this.emiSchedule.find((e) => e._id === emiId)
      if (!emi) return

      this.selectedEmi = emi
      this.editEmiDialog = true
    },

    handleEmiUpdate(payload) {
      // Emit event to parent component to handle the update
      this.$emit('update-emi', payload)
    },

    calculateMinimumAmountForEmi(emi) {
      const interestAmount = parseFloat(emi.interestAmount) || 0
      const overdueInfo = this.getOverdueInfo(emi)
      const lateFeeAmount = overdueInfo.lateFeeAmount || 0
      return interestAmount + lateFeeAmount
    },

    calculatePartnerShare(emi) {
      return emi.interestAmount - this.calculateBrokerShare(emi)
    },
    calculateBrokerShare(emi) {
      return calculateBrokerShareAmount(
        emi,
        this.revenueShare,
        this.getTotalPrincipal(),
        this.emiSchedule?.length
      )
    },
    getTotalPrincipal() {
      if (this.emiSchedule.length > 0) {
        const firstEmi = this.emiSchedule[0]
        return parseFloat(firstEmi.outstandingAmount) || 0
      }
      return 0
    },

    calculateActualPartnerAmount(emi) {
      const emiAmount = parseFloat(emi.emiAmount) || 0
      const brokerAmount = this.calculateBrokerShare(emi)
      return emiAmount - brokerAmount
    },

    getActivePayments(emi) {
      if (!emi || !emi.emiPayments) return []
      return emi.emiPayments.filter((p) => !p.isDeleted)
    },

    getReceiptTotalAmount(payment) {
      if (!payment) return 0
      const voucherNo = payment.voucherNo || payment.receiptNo || payment.transactionId || null

      if (voucherNo) {
        let total = 0
        let matchedAny = false

        // Sum across all EMIs sharing this voucherNo (base amount + late fee + collection charges)
        const emiList = this.emiSchedule || []
        emiList.forEach((e) => {
          const payments = e.emiPayments || []
          payments.forEach((p) => {
            if (p.voucherNo === voucherNo && !p.isDeleted) {
              matchedAny = true
              total +=
                (parseFloat(p.amount) || 0) +
                (parseFloat(p.latePaymentAmount) || 0) +
                (parseFloat(p.collectionCharges) || 0)
            }
          })
        })

        // Sum across matching additional charges (VAS) sharing this voucherNo
        const rawCharges =
          this.selectedCase?.loanDetails?.additionalCharges ||
          this.loanDetails?.additionalCharges ||
          []
        rawCharges.forEach((c) => {
          if (c.voucherNo === voucherNo && !c.isDeleted) {
            matchedAny = true
            total += parseFloat(c.paidAmount) || parseFloat(c.amount) || 0
          }
        })

        if (matchedAny && total > 0) {
          return total
        }

        // Check in loanDetails.paymentSummaries
        const summaryMatch = (this.loanDetails?.paymentSummaries || []).find(
          (s) => s.voucherNo === voucherNo && !s.isDeleted
        )
        if (
          summaryMatch &&
          (parseFloat(summaryMatch.amountPaid) || parseFloat(summaryMatch.amount))
        ) {
          return parseFloat(summaryMatch.amountPaid) || parseFloat(summaryMatch.amount)
        }
      }

      // Fallback for single payment object
      return (
        parseFloat(payment.totalAmount) ||
        parseFloat(payment.totalPaidAmount) ||
        parseFloat(payment.amountPaid) ||
        (parseFloat(payment.amount) || 0) +
          (parseFloat(payment.latePaymentAmount) || 0) +
          (parseFloat(payment.collectionCharges) || 0)
      )
    },

    getEmiTotalReceiptsAmount(emi) {
      const activePayments = this.getActivePayments(emi)
      return activePayments.reduce((sum, p) => sum + this.getReceiptTotalAmount(p), 0)
    },

    async generateReceipt(emi, paymentOrPayments, emiIndex, mode = 'download') {
      if (!this.selectedCase) {
        this.$emit('show-notification', {
          message: 'Case details not available for receipt generation',
          color: 'error'
        })
        return
      }

      const payments = Array.isArray(paymentOrPayments)
        ? paymentOrPayments
        : paymentOrPayments
        ? [paymentOrPayments]
        : this.getActivePayments(emi)

      if (payments.length === 0) {
        this.$emit('show-notification', {
          message: 'No active receipts found for this EMI',
          color: 'warning'
        })
        return
      }

      const loadingKey = Array.isArray(paymentOrPayments)
        ? `emi-${emiIndex}`
        : paymentOrPayments?._id || `emi-${emiIndex}`

      this.isGeneratingPDF = loadingKey

      try {
        const orgData = JSON.parse(localStorage.getItem('orgData')) || {}
        const orgName = orgData.name || 'Zavron Finance Pvt Ltd'

        let orgAddress = ''
        if (orgData.organization_address) {
          const addr = orgData.organization_address
          orgAddress = [
            addr.address_line_1,
            addr.address_line_2,
            addr.city,
            addr.state,
            addr.pincode
          ]
            .filter(Boolean)
            .join(', ')
        }
        if (!orgAddress) {
          orgAddress =
            '125, Zavron finance pvt ltd near mure memorial hospital maharajbagh road, sitabuldi, Nagpur, Maharashtra'
        }

        const branchVal = this.selectedCase?.branch
        let branchName = ''
        if (branchVal && typeof branchVal === 'object') {
          branchName = branchVal.branchName || branchVal.name || ''
        }
        if (!branchName) branchName = 'NAGPUR'
        const branchPhone = orgData.phone || '0712-2520007'

        const customerName = this.selectedCase.customer?.customerFullName || 'Unknown'
        const agreementDate =
          this.selectedCase.caseStartDate || this.selectedCase.loanDetails?.sanctionDate || ''
        const agreementNo = this.selectedCase.caseNo || ''

        const formatReceiptCurrency = (value) => {
          return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(value || 0)
        }

        const docContent = []

        payments.forEach((payment, pIdx) => {
          const voucherNo =
            payment.voucherNo || payment.receiptNo || payment.transactionId || 'HOR0000000'

          // Find all matching payments across the entire emiSchedule sharing this voucherNo
          const adjustedItems = []
          let totalAmountPaid = 0

          this.emiSchedule.forEach((e, idx) => {
            const matchingPayments = (e.emiPayments || []).filter(
              (p) => p.voucherNo === voucherNo && !p.isDeleted
            )
            matchingPayments.forEach((mp) => {
              const baseAmt = parseFloat(mp.amount) || 0
              adjustedItems.push({
                emiNo: idx + 1,
                emiDueDate: e.dueDate,
                baseAmount: baseAmt,
                latePaymentAmount: parseFloat(mp.latePaymentAmount) || 0,
                collectionCharges: parseFloat(mp.collectionCharges) || 0,
                totalAmount: baseAmt
              })
              totalAmountPaid +=
                baseAmt +
                (parseFloat(mp.latePaymentAmount) || 0) +
                (parseFloat(mp.collectionCharges) || 0)
            })
          })

          // Find matching additional charges (VAS) sharing this voucherNo
          const matchingCharges = []
          const rawCharges = this.selectedCase?.loanDetails?.additionalCharges || []
          rawCharges.forEach((c) => {
            if (c.voucherNo === voucherNo && !c.isDeleted) {
              const chargeAmt = parseFloat(c.paidAmount) || parseFloat(c.amount) || 0
              matchingCharges.push({
                name: c.name || c.feeType || 'Charge',
                dueDate: c.dueDate,
                feeType: c.feeType || 'VAS',
                amount: chargeAmt
              })
              totalAmountPaid += chargeAmt
            }
          })

          // Fallback if not found in loop
          if (adjustedItems.length === 0 && matchingCharges.length === 0) {
            const baseAmt = parseFloat(payment.amount) || 0
            adjustedItems.push({
              emiNo: emiIndex + 1,
              emiDueDate: emi.dueDate,
              baseAmount: baseAmt,
              latePaymentAmount: parseFloat(payment.latePaymentAmount) || 0,
              collectionCharges: parseFloat(payment.collectionCharges) || 0,
              totalAmount: baseAmt
            })
            totalAmountPaid =
              baseAmt +
              (parseFloat(payment.latePaymentAmount) || 0) +
              (parseFloat(payment.collectionCharges) || 0)
          }

          let cashierName = ''
          if (payment.receivedBy && typeof payment.receivedBy === 'object') {
            cashierName = `${payment.receivedBy.firstName || ''} ${
              payment.receivedBy.lastName || ''
            }`.trim()
          }
          if (!cashierName) {
            cashierName = payment.processedBy || ''
          }

          const paymentMode = payment.paymentMode || payment.paymentMethod || 'Cash'
          const instrumentNo = payment.utrNo || payment.chequeNo || '0'

          const buildCopyDefinition = (copyTitle) => {
            const tableBody = [
              [
                { text: '#', bold: true, fontSize: 8.5 },
                { text: 'Due Date', bold: true, fontSize: 8.5 },
                { text: 'Desc', bold: true, fontSize: 8.5, alignment: 'right' },
                { text: 'Amount', bold: true, fontSize: 8.5, alignment: 'right' }
              ]
            ]

            adjustedItems.forEach((item) => {
              tableBody.push([
                { text: `(Part) ${item.emiNo} / ${this.emiSchedule.length}`, fontSize: 8 },
                {
                  text: item.emiDueDate ? this.$dayjs(item.emiDueDate).format('DD-MM-YYYY') : '-',
                  fontSize: 8
                },
                { text: 'EMI', fontSize: 8, alignment: 'right' },
                { text: formatReceiptCurrency(item.baseAmount), fontSize: 8, alignment: 'right' }
              ])
              if (item.latePaymentAmount > 0) {
                tableBody.push([
                  '',
                  '',
                  { text: '*LP Charges', fontSize: 8, alignment: 'right', italics: true },
                  {
                    text: formatReceiptCurrency(item.latePaymentAmount),
                    fontSize: 8,
                    alignment: 'right'
                  }
                ])
              }
              if (item.collectionCharges > 0) {
                tableBody.push([
                  '',
                  '',
                  { text: 'Collection Charges', fontSize: 8, alignment: 'right', italics: true },
                  {
                    text: formatReceiptCurrency(item.collectionCharges),
                    fontSize: 8,
                    alignment: 'right'
                  }
                ])
              }
            })

            matchingCharges.forEach((mc) => {
              tableBody.push([
                { text: mc.name, fontSize: 8 },
                '',
                { text: mc.feeType, fontSize: 8, alignment: 'right' },
                { text: formatReceiptCurrency(mc.amount), fontSize: 8, alignment: 'right' }
              ])
            })

            return [
              { text: copyTitle, fontSize: 8, italics: true, bold: true, margin: [0, 0, 0, 4] },
              {
                stack: [
                  { text: orgName, fontSize: 13, bold: true, alignment: 'center' },
                  { text: orgAddress, fontSize: 7.5, alignment: 'center', margin: [10, 2, 10, 2] },
                  {
                    text: `Branch:${branchName} . Ph:${branchPhone}`,
                    fontSize: 8,
                    bold: true,
                    alignment: 'center'
                  }
                ],
                margin: [0, 0, 0, 8]
              },
              {
                canvas: [
                  {
                    type: 'line',
                    x1: 0,
                    y1: 0,
                    x2: 380,
                    y2: 0,
                    lineWidth: 0.5,
                    strokeColor: '#cccccc'
                  }
                ],
                margin: [0, 0, 0, 6]
              },
              {
                table: {
                  widths: ['18%', '32%', '20%', '30%'],
                  body: [
                    [
                      { text: 'Date:', bold: true, fontSize: 8.5 },
                      {
                        text: payment.date ? this.$dayjs(payment.date).format('DD-MM-YYYY') : '-',
                        fontSize: 8.5
                      },
                      { text: 'Voucher No:', bold: true, fontSize: 8.5 },
                      { text: voucherNo, fontSize: 8.5 }
                    ],
                    [
                      { text: 'Customer:', bold: true, fontSize: 8.5 },
                      { text: customerName.toUpperCase(), bold: true, fontSize: 9.5, colSpan: 3 },
                      '',
                      ''
                    ],
                    [
                      { text: 'Instrument No:', bold: true, fontSize: 8.5 },
                      { text: instrumentNo, fontSize: 8.5, colSpan: 3 },
                      '',
                      ''
                    ],
                    [
                      { text: 'Loan Date:', bold: true, fontSize: 8.5 },
                      {
                        text: agreementDate ? this.$dayjs(agreementDate).format('DD-MM-YYYY') : '-',
                        fontSize: 8.5
                      },
                      { text: 'Loan No:', bold: true, fontSize: 8.5 },
                      { text: agreementNo, fontSize: 8.5 }
                    ]
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0.5,
                  hLineColor: () => '#cccccc',
                  vLineColor: () => '#cccccc',
                  paddingLeft: () => 6,
                  paddingRight: () => 6,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                },
                margin: [0, 0, 0, 8]
              },
              {
                table: {
                  widths: ['15%', '25%', '40%', '20%'],
                  body: tableBody
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0.5,
                  hLineColor: () => '#cccccc',
                  vLineColor: () => '#cccccc',
                  paddingLeft: () => 6,
                  paddingRight: () => 6,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                },
                margin: [0, 0, 0, 6]
              },
              {
                table: {
                  widths: ['60%', '20%', '20%'],
                  body: [
                    [
                      payment.comments || '',
                      { text: 'Total', bold: true, fontSize: 9, alignment: 'right' },
                      {
                        text: `₹${formatReceiptCurrency(totalAmountPaid)}`,
                        bold: true,
                        fontSize: 9,
                        alignment: 'right'
                      }
                    ]
                  ]
                },
                layout: {
                  hLineWidth: () => 1,
                  vLineWidth: () => 0,
                  hLineColor: () => '#000000',
                  paddingLeft: () => 6,
                  paddingRight: () => 6,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                },
                margin: [0, 0, 0, 8]
              },
              {
                table: {
                  widths: ['25%', '25%', '25%', '25%'],
                  body: [
                    [
                      { text: 'Payment Mode:', bold: true, fontSize: 8.5 },
                      { text: paymentMode, fontSize: 8.5 },
                      { text: 'Cashier:', bold: true, fontSize: 8.5 },
                      { text: cashierName || '', fontSize: 8.5 }
                    ]
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0.5,
                  hLineColor: () => '#cccccc',
                  vLineColor: () => '#cccccc',
                  paddingLeft: () => 6,
                  paddingRight: () => 6,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                },
                margin: [0, 4, 0, 12]
              },
              {
                columns: [
                  {
                    stack: [
                      { text: '', margin: [0, 20, 0, 0] },
                      { text: 'Customer Signature', fontSize: 8, alignment: 'center' }
                    ],
                    width: '40%'
                  },
                  { width: '20%', text: '' },
                  {
                    stack: [
                      { text: `For ${orgName}`, fontSize: 7.5, bold: true, alignment: 'center' },
                      { text: '', margin: [0, 15, 0, 0] },
                      { text: 'Cashier', fontSize: 8, alignment: 'center' }
                    ],
                    width: '40%'
                  }
                ],
                margin: [0, 10, 0, 10]
              },
              {
                canvas: [
                  {
                    type: 'line',
                    x1: 0,
                    y1: 0,
                    x2: 380,
                    y2: 0,
                    lineWidth: 0.5,
                    strokeColor: '#eeeeee'
                  }
                ],
                margin: [0, 5, 0, 5]
              },
              {
                columns: [
                  {
                    text: `Created Date: ${this.$dayjs(payment.createdAt || payment.date).format(
                      'DD-MM-YYYY HH:mm:ss'
                    )}`,
                    fontSize: 7,
                    color: '#666666'
                  },
                  {
                    text: 'This is System Generated Receipt.',
                    fontSize: 7.5,
                    color: '#444444',
                    bold: true,
                    alignment: 'center'
                  },
                  {
                    text: `Printed: ${this.$dayjs().format('DD-MM-YYYY HH:mm:ss')}`,
                    fontSize: 7,
                    color: '#666666',
                    alignment: 'right'
                  }
                ]
              }
            ]
          }

          docContent.push({
            pageBreak: pIdx > 0 ? 'before' : undefined,
            columns: [
              {
                width: '48%',
                stack: buildCopyDefinition('Customer Copy')
              },
              {
                width: '4%',
                alignment: 'center',
                stack: [
                  {
                    canvas: [
                      {
                        type: 'line',
                        x1: 10,
                        y1: 0,
                        x2: 10,
                        y2: 380,
                        lineWidth: 0.5,
                        strokeColor: '#999999',
                        lineDash: { l: 5, space: 3 }
                      }
                    ]
                  }
                ]
              },
              {
                width: '48%',
                stack: buildCopyDefinition('Official Copy')
              }
            ]
          })
        })

        const docDefinition = {
          pageSize: 'A4',
          pageOrientation: 'landscape',
          pageMargins: [20, 20, 20, 20],
          defaultStyle: {
            font: 'Roboto'
          },
          content: docContent
        }

        const filename =
          payments.length > 1
            ? `Receipts_${agreementNo}_EMI_${emiIndex + 1}_All.pdf`
            : `Receipt_${agreementNo}_EMI_${emiIndex + 1}.pdf`

        if (mode === 'download') {
          pdfMake.createPdf(docDefinition).download(filename)
          this.$emit('show-notification', {
            message:
              payments.length > 1
                ? `All ${payments.length} receipts downloaded in 1 PDF`
                : 'Receipt downloaded successfully',
            color: 'success'
          })
        } else {
          pdfMake.createPdf(docDefinition).open()
        }
      } catch (error) {
        console.error('Error generating PDF receipt:', error)
        this.$emit('show-notification', {
          message: 'Error generating receipt PDF',
          color: 'error'
        })
      } finally {
        this.isGeneratingPDF = null
      }
    },
    exportToExcel() {
      try {
        const data = this.emiSchedule.map((emi, index) => {
          const overdueInfo = this.getOverdueInfo(emi)
          return {
            'EMI No.': index + 1,
            'Due Date': this.formatDate(emi.dueDate),
            'Interest Rate (%)': emi.atRateOfInterest,
            'EMI Amount': parseFloat(Number(emi.emiAmount || 0).toFixed(2)),
            Principal: parseFloat(Number(emi.principalAmount || 0).toFixed(2)),
            Interest: parseFloat(Number(emi.interestAmount || 0).toFixed(2)),
            'Principal Outstanding': parseFloat(Number(emi.outstandingAmount || 0).toFixed(2)),
            'Late Fee': parseFloat(Number(overdueInfo.lateFeeAmount || 0).toFixed(2)),
            'EMI Status': emi.emiStatus,
            'PDD Status': this.getPDDStatus(emi.activities).status,
            'Payment Date': emi.paymentDate ? this.formatDate(emi.paymentDate) : 'N/A'
          }
        })

        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'EMI Schedule')

        // Generate dynamic filename
        const filename = `EMI_Schedule_${
          this.selectedCase?.caseNo || 'Export'
        }_${new Date().getTime()}.xlsx`

        XLSX.writeFile(workbook, filename)

        this.$emit('show-notification', {
          message: 'EMI Schedule exported successfully',
          color: 'success'
        })
      } catch (error) {
        console.error('Error exporting to Excel:', error)
        this.$emit('show-notification', {
          message: 'Error exporting EMI schedule',
          color: 'error'
        })
      }
    },
    confirmRevert(emi, payment) {
      this.selectedPaymentToRevert = {
        ...payment,
        // LoanPaymentSummary docs use `amountPaid`; emiPayments entries use `amount`
        amount: payment?.amount ?? payment?.amountPaid ?? 0,
        emiId: emi ? emi._id : null
      }
      this.revertDialog = true
    },
    submitRevert() {
      if (!this.selectedPaymentToRevert) return
      this.reverting = true
      this.$emit('revert-installment', {
        voucherNo: this.selectedPaymentToRevert.voucherNo
      })
      this.revertDialog = false
      this.reverting = false
    }
  }
}
</script>

<style scoped>
.loan-emi-schedule {
  border-radius: 5px;
  padding: 20px;
}

.activity-item {
  transition: all 0.2s ease;
}

.activity-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark-mode .loan-emi-schedule {
  background-color: #1e1e1e;
  color: #e0e0e0;
}

.strike-through-text {
  text-decoration: line-through;
}
</style>
